import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { GridRowParams } from "@mui/x-data-grid";
import RepairPopup from "../RepairPopup/RepairPopup";
import RepairsTable from "../RepairsTable/RepairsTable";
import { getIncidentsTableColumns } from "../../../data/FormColumns/Fleet/incidentsTableColumns";
import { getIncidentRowClassName } from "../../../helpers/getRowClassName";
import { useEffect, useState } from "react";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import "./IncidentsTable.css";
import * as signalR from "@microsoft/signalr";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import PersistedDataGrid from "../../PersistedDataGrid/PersistedDataGrid";
import IncidentProofUploadModal from "../IncidentProofUploadModal/IncidentProofUploadModal";
import IncidentProofPreviewModal from "../IncidentProofPreviewModal/IncidentProofPreviewModal";

const fetchIncidents = async (
  token: string | null | undefined,
  month?: number,
  year?: number,
) => {
  const { data } = await axios.get(`${apiVariable}/incident`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: {
      month,
      year,
    },
  });
  return data;
};

const fetchIncidentByInvoiceNumber = async (
  token: string | null | undefined,
  invoiceNumber: string,
) => {
  const { data } = await axios.post(
    `${apiVariable}/incidentByInvoiceNumber`,
    `"${invoiceNumber}"`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );
  return data;
};

const IncidentsTable = () => {
  const [open, setOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState({});
  const [openedIncidentId, setOpenedIncidentId] = useState<string | null>(null);
  const [lastOpenedIncidentId, setLastOpenedIncidentId] = useState<
    string | null
  >(null);
  const [isIncidentCharged, setIsIncidentCharged] = useState(false);
  const [affectedTruck, setAffectedTruck] = useState(null);
  const [affectedTrailer, setAffectedTrailer] = useState(null);
  const [openRepairTable, setOpenRepairTable] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number>(
    new Date().getMonth() + 1,
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear(),
  );
  const [showAll, setShowAll] = useState(false);
  const [proofUploadOpen, setProofUploadOpen] = useState(false);
  const [proofPreviewOpen, setProofPreviewOpen] = useState(false);
  const [selectedIncidentForProofs, setSelectedIncidentForProofs] = useState<
    any | null
  >(null);
  const queryClient = useQueryClient();
  const handleSnackbarClose = () => setStatus("idle");
  const { token } = useAuth();

  const {
    data: incidents,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["incidents", selectedMonth, selectedYear, showAll],
    queryFn: () =>
      fetchIncidents(
        token,
        showAll ? undefined : selectedMonth,
        showAll ? undefined : selectedYear,
      ),
  });

  useEffect(() => {
    if (open === false) {
      setIsIncidentCharged(false);
    }
  }, [open]);

  useEffect(() => {
    const repairConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiVariable}/repairHub`, {
        accessTokenFactory: () => token || "",
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.None)
      .build();

    repairConnection.on("RepairUpdated", () => {
      queryClient.invalidateQueries({ queryKey: ["repairs"] });
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
      queryClient.invalidateQueries({ queryKey: ["asset"] });
    });

    repairConnection.start();

    return () => {
      repairConnection.stop();
    };
  }, [token, queryClient]);

  const handleSearchSubmit = () => {
    if (searchTerm.trim() !== "") {
      fetchIncidentByInvoiceNumber(token, searchTerm)
        .then((data) => {
          queryClient.setQueryData(
            ["incidents", selectedMonth, selectedYear, showAll],
            [data],
          );
          setStatus("success");
          setErrorMessage(null);
        })
        .catch(() => {
          queryClient.setQueryData(
            ["incidents", selectedMonth, selectedYear, showAll],
            [],
          );
          setStatus("error");
          setErrorMessage("Invoice number not found");
        });
    } else {
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  const handleOpen = (params: GridRowParams) => {
    setSelectedIncident(params.row);
    setOpenedIncidentId(params.row.id ?? null);
    setAffectedTruck(params.row.truckId);
    setAffectedTrailer(params.row.trailerId);
    const driverCharged =
      typeof params.row.isDriverCharged === "boolean"
        ? params.row.isDriverCharged
        : params.row.isCharged;
    setIsIncidentCharged(!!driverCharged);
    if (params.row.incidentStatus === "notStarted") {
      setOpen(true);
    } else {
      setOpenRepairTable(true);
    }
  };

  const handleIncidentDetailsClose = (isOpen: boolean) => {
    if (!isOpen && openedIncidentId) {
      setLastOpenedIncidentId(openedIncidentId);
      setOpenedIncidentId(null);
    }

    setOpen(isOpen);
  };

  const handleRepairsTableClose = (isOpen: boolean) => {
    if (!isOpen && openedIncidentId) {
      setLastOpenedIncidentId(openedIncidentId);
      setOpenedIncidentId(null);
    }

    setOpenRepairTable(isOpen);
  };

  const handleOpenProofUpload = (incidentRow: any) => {
    setSelectedIncidentForProofs(incidentRow);
    setProofUploadOpen(true);
  };

  const handleOpenProofPreview = (incidentRow: any) => {
    setSelectedIncidentForProofs(incidentRow);
    setProofPreviewOpen(true);
  };

  const [processedIncidents, setProcessedIncidents] = useState<any>([]);

  useEffect(() => {
    if (incidents) {
      const processedData = processData(incidents);
      setProcessedIncidents(processedData);
    }
  }, [incidents]);

  const processData = (data: any) => {
    const incidentMap = new Map();
    data.forEach((incident: any) => {
      incidentMap.set(incident.id, incident);
    });

    const processedData = data.map((incident: any) => {
      const isChild = !!incident.incidentParentId;
      const parentExists = isChild
        ? incidentMap.has(incident.incidentParentId)
        : false;

      let level = 0;
      let isVisible = true;
      if (isChild && parentExists) {
        level = 1;
        isVisible = false;
      } else {
        level = 0;
        isVisible = true;
      }

      return {
        ...incident,
        level,
        isExpanded: false,
        isVisible,
        totalFinalCost: incident.finalCost || 0,
        totalComplaints: incident.complaint || 0,
      };
    });

    processedData.forEach((incident: any) => {
      if (incident.level === 0) {
        const children = processedData.filter(
          (childIncident: any) =>
            childIncident.incidentParentId === incident.id,
        );
        const childrenFinalCost = children.reduce(
          (acc: any, child: any) => acc + (child.finalCost || 0),
          0,
        );
        const childrenComplaints = children.reduce(
          (acc: any, child: any) => acc + (child.complaint || 0),
          children.length > 0 ? ", " : "",
        );
        incident.totalFinalCost += childrenFinalCost;
        incident.totalComplaints += childrenComplaints;
      }
    });

    return processedData;
  };

  const handleExpandClick = (rowId: any) => {
    setProcessedIncidents((prevData: any) =>
      prevData.map((row: any) => {
        if (row.id === rowId) {
          return { ...row, isExpanded: !row.isExpanded };
        } else if (row.incidentParentId === rowId) {
          return { ...row, isVisible: !row.isVisible };
        }
        return row;
      }),
    );
  };
  const buildVisibleRows = (data: any[]) => {
    const incidentMap = new Map();
    data.forEach((incident) => {
      incidentMap.set(incident.id, incident);
    });

    const visibleRows: any[] = [];

    const addIncidentAndChildren = (incident: any) => {
      if (incident.isVisible) {
        visibleRows.push(incident);
        if (incident.isExpanded) {
          const children = data.filter(
            (child: any) =>
              child.incidentParentId === incident.id && child.isVisible,
          );
          for (const child of children) {
            addIncidentAndChildren(child);
          }
        }
      }
    };

    data
      .filter((incident) => !incident.incidentParentId)
      .forEach((incident) => {
        addIncidentAndChildren(incident);
      });

    return visibleRows;
  };

  const visibleRows = buildVisibleRows(processedIncidents);

  const buildExportRows = (data: any[]) => {
    const incidentById = new Map(
      data.map((incident) => [incident.id, incident]),
    );

    return Array.from(incidentById.values()).map((incident: any) => {
      const parentIncident = incident.incidentParentId
        ? incidentById.get(incident.incidentParentId)
        : null;

      return {
        ...incident,
        incidentIdentification: incident.id,
        incidentHierarchy: incident.incidentParentId
          ? "Split/Child Incident"
          : "Parent Incident",
        parentIncidentId: incident.incidentParentId || "",
        parentIncidentComplaint: parentIncident?.complaint ?? "",
      };
    });
  };

  const incidentExportRows = buildExportRows(processedIncidents);

  const sanitizeIncidentExportRows = (rows: any[]) =>
    rows.map((incident) =>
      Object.fromEntries(
        Object.entries(incident).filter(([key]) => {
          if (key === "parentIncidentId") return true;
          return !/Id$/i.test(key);
        }),
      ),
    );

  return (
    <>
      <Box className="incidents-table-container" height={900}>
        <Box
          className="table-controls"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "space-between",
            alignItems: "flex-end",
            mb: 2,
          }}
        >
          <Grid container spacing={2} sx={{ width: "auto" }}>
            <Grid item>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Month</InputLabel>
                <Select
                  value={selectedMonth}
                  label="Month"
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  disabled={showAll}
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {new Date(2000, i).toLocaleString("default", {
                        month: "long",
                      })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Year</InputLabel>
                <Select
                  value={selectedYear}
                  label="Year"
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  disabled={showAll}
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() - 2 + i;
                    return (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showAll}
                    onChange={(e) => {
                      setShowAll(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedMonth(new Date().getMonth() + 1);
                        setSelectedYear(new Date().getFullYear());
                      }
                    }}
                  />
                }
                label="Show All Incidents"
              />
            </Grid>
          </Grid>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              position: "relative",
            }}
          >
            <TextField
              label="Search"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by Invoice number"
              sx={{ maxWidth: "100%", width: 400 }}
              onKeyDown={handleKeyDown}
            />
          </Box>
        </Box>
        {isLoading ? (
          <Box className="table-loader">
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box className="table-error">
            <Typography color="error">Error: {error.message}</Typography>
          </Box>
        ) : (
          <PersistedDataGrid
            tableName="incident"
            rows={visibleRows}
            exportRows={incidentExportRows}
            sanitizeForExport={sanitizeIncidentExportRows}
            columns={getIncidentsTableColumns(
              processedIncidents,
              handleExpandClick,
              handleOpenProofUpload,
              handleOpenProofPreview,
            )}
            compactViewConfig={{
              primaryField: "complaint",
              secondaryFields: ["truckUnitNumber", "trailerUnitNumber"],
              detailFields: [
                "openDate",
                "driverName",
                "driverType",
                "incidentType",
                "location",
                "receivedBy",
                "finalCost",
                "rentCompanyName",
                "isCompanyCharged",
                "chargedCompanyAmount",
                "isDriverCharged",
                "chargedDriverAmount",
              ],
              actionFields: ["notes", "proofs", "update", "delete"],
              statusField: "incidentStatus",
            }}
            getRowClassName={(params) =>
              getIncidentRowClassName(params, lastOpenedIncidentId)
            }
            onRowClick={handleOpen}
            sx={{ height: 700 }}
          />
        )}

        <RepairPopup
          isOpened={open}
          setIsOpened={handleIncidentDetailsClose}
          incident={selectedIncident}
          affectedTruckId={affectedTruck}
          affectedTrailerId={affectedTrailer}
        />
        <RepairsTable
          incident={selectedIncident}
          open={openRepairTable}
          setOpen={handleRepairsTableClose}
          affectedTruckId={affectedTruck}
          affectedTrailerId={affectedTrailer}
          isIncidentCharged={isIncidentCharged}
        />

        {selectedIncidentForProofs && (
          <IncidentProofUploadModal
            open={proofUploadOpen}
            onClose={() => setProofUploadOpen(false)}
            incident={selectedIncidentForProofs}
          />
        )}
        {selectedIncidentForProofs && (
          <IncidentProofPreviewModal
            open={proofPreviewOpen}
            onClose={() => setProofPreviewOpen(false)}
            incident={selectedIncidentForProofs}
          />
        )}
        <StatusSnackbar
          status={status}
          successMessage={"Invoice number found"}
          errorMessage={errorMessage}
          handleClose={handleSnackbarClose}
        />
      </Box>
    </>
  );
};

export default IncidentsTable;
